import { Component, ElementRef, ViewChild, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService, showToast } from './api.service';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';

type ReportStatus = 'Draft' | 'Ready for Review' | 'Finalised' | 'Sent' | 'Archived';
type ReferenceType = 'Manual' | 'Job' | 'Project';
type ItemCategory = 'Finding' | 'Evidence' | 'Damage' | 'Repair' | 'Recommendation' | 'Other';
type AttachTab = 'Project' | 'Job';
type CropDragMode = 'move' | 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se';

interface ReportItem {
  id?: number;
  category: ItemCategory;
  title: string;
  location: string;
  description: string;
  photoUrl: string | null;
  photoPreview: string | null;
  includeInPdf: boolean;
}

interface InsuranceReport {
  id: number;
  report_no: string;
  status: ReportStatus;
  reference_id?: string | null;
  model_type?: ReferenceType;
  customer_name?: string;
  site_address?: string;
  incident_type?: string;
  updated_at?: string;
  created_at?: string;
  item_count?: number;
}

@Component({
  selector: 'app-admin-insurance-doc',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin-insurance-doc.component.html',
  styleUrl: './admin-insurance-doc.component.css'
})
export class AdminInsuranceDocComponent {
  @ViewChild('cropSurface') private cropSurface?: ElementRef<HTMLElement>;
  private readonly maxOriginalPhotoBytes = 100 * 1024 * 1024;
  private readonly maxPreparedPhotoBase64Length = 67.5 * 1024 * 1024;

  private api = inject(ApiService);
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  reportItems = signal<ReportItem[]>([]);
  activeReportId = signal<number | null>(null);
  reportNo = signal<string>('New report');
  status = signal<ReportStatus>('Draft');
  referenceId = signal<string | null>(null);
  modelType = signal<ReferenceType>('Manual');

  customerName = signal('');
  customerPhone = signal('');
  customerEmail = signal('');
  jobAddress = signal('');
  claimNumber = signal('');
  insurer = signal('');
  policyNumber = signal('');
  incidentType = signal('');
  attendanceDate = signal(new Date().toISOString().split('T')[0]);
  attendanceTime = signal('');
  technicianName = signal('');
  causeSummary = signal('');
  workCompleted = signal('');
  damageObserved = signal('');
  recommendations = signal('');
  limitations = signal('Observations are based on accessible areas at the time of attendance. Hidden damage, concealed pipework, and building substrate conditions may require separate assessment by the relevant specialist.');

  isModalOpen = signal(false);
  editingIndex = signal(-1);
  modalCategory = signal<ItemCategory>('Finding');
  modalTitle = signal('');
  modalLocation = signal('');
  modalDescription = signal('');
  modalPhotoBase64 = signal<string | null>(null);
  modalPhotoPreview = signal<string | null>(null);
  modalIncludeInPdf = signal(true);
  cropSourceDataUrl = signal<string | null>(null);
  cropSourceWidth = signal(0);
  cropSourceHeight = signal(0);
  cropX = signal(0);
  cropY = signal(0);
  cropWidth = signal(100);
  cropHeight = signal(100);
  cropEditorOpen = signal(false);
  cropDragMode: CropDragMode | null = null;
  private cropDragStart = { x: 0, y: 0 };
  private cropDragBox = { x: 0, y: 0, width: 100, height: 100 };

  isRefining = signal(false);
  isGenerating = signal(false);
  isSaving = signal(false);
  isSendingEmail = signal(false);
  isLoadingReport = signal(false);
  isPreparingPhoto = signal(false);

  attachModalOpen = signal(false);
  attachTab = signal<AttachTab>('Project');
  projects = signal<any[]>([]);
  jobsList = signal<any[]>([]);
  isLoadingAttachList = signal(false);

  readonly statuses: ReportStatus[] = ['Draft', 'Ready for Review', 'Finalised', 'Sent', 'Archived'];
  readonly categories: ItemCategory[] = ['Finding', 'Evidence', 'Damage', 'Repair', 'Recommendation', 'Other'];

  itemCount = computed(() => this.reportItems().filter(item => item.includeInPdf).length);
  cropBoxStyle = computed(() => ({
    left: `${this.cropX()}%`,
    top: `${this.cropY()}%`,
    width: `${this.cropWidth()}%`,
    height: `${this.cropHeight()}%`
  }));
  attachedLabel = computed(() => {
    const ref = this.referenceId();
    if (!ref || this.modelType() === 'Manual') return 'Unattached';
    return `${this.modelType()} - ${ref}`;
  });

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = Number(params.get('id'));
      if (Number.isFinite(id) && id > 0) {
        this.loadReport(id);
      } else {
        this.router.navigate(['/admin/ai/insurance']);
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/admin/ai/insurance']);
  }

  private loadImageInfo(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
      img.onerror = () => reject(new Error('Could not read image'));
      img.src = dataUrl;
    });
  }

  private cropImage(dataUrl: string, crop: { x: number; y: number; width: number; height: number }, maxSide = 1800, quality = 0.86): Promise<{ base64: string; dataUrl: string }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const naturalWidth = img.naturalWidth || img.width;
        const naturalHeight = img.naturalHeight || img.height;
        const sx = Math.round((crop.x / 100) * naturalWidth);
        const sy = Math.round((crop.y / 100) * naturalHeight);
        const sw = Math.max(1, Math.round((crop.width / 100) * naturalWidth));
        const sh = Math.max(1, Math.round((crop.height / 100) * naturalHeight));
        const ratio = Math.min(1, maxSide / Math.max(sw, sh));
        const width = Math.max(1, Math.round(sw * ratio));
        const height = Math.max(1, Math.round(sh * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not prepare crop'));
          return;
        }
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
        const croppedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve({ dataUrl: croppedDataUrl, base64: croppedDataUrl.split(',')[1] || croppedDataUrl });
      };
      img.onerror = () => reject(new Error('Could not crop image'));
      img.src = dataUrl;
    });
  }

  newReport(): void {
    this.activeReportId.set(null);
    this.reportNo.set('New report');
    this.status.set('Draft');
    this.referenceId.set(null);
    this.modelType.set('Manual');
    this.customerName.set('');
    this.customerPhone.set('');
    this.customerEmail.set('');
    this.jobAddress.set('');
    this.claimNumber.set('');
    this.insurer.set('');
    this.policyNumber.set('');
    this.incidentType.set('');
    this.attendanceDate.set(new Date().toISOString().split('T')[0]);
    this.attendanceTime.set('');
    this.technicianName.set('');
    this.causeSummary.set('');
    this.workCompleted.set('');
    this.damageObserved.set('');
    this.recommendations.set('');
    this.limitations.set('Observations are based on accessible areas at the time of attendance. Hidden damage, concealed pipework, and building substrate conditions may require separate assessment by the relevant specialist.');
    this.reportItems.set([]);
  }

  loadReport(id: number): void {
    this.isLoadingReport.set(true);
    this.http.get<any>(`${this.api.baseUrl}/ai/get_insurance_report.php?id=${id}`, { headers: (this.api as any).getHeaders() })
      .subscribe({
        next: (res) => {
          this.isLoadingReport.set(false);
          if (!res?.success || !res.data?.report) {
            showToast('error', 'Report not found');
            return;
          }
          const report = res.data.report;
          this.activeReportId.set(Number(report.id));
          this.reportNo.set(report.report_no || `INS-${report.id}`);
          this.status.set(report.status || 'Draft');
          this.referenceId.set(report.reference_id || null);
          this.modelType.set(report.model_type || 'Manual');
          this.customerName.set(report.customer_name || '');
          this.customerPhone.set(report.customer_phone || '');
          this.customerEmail.set(report.customer_email || '');
          this.jobAddress.set(report.site_address || '');
          this.claimNumber.set(report.claim_number || '');
          this.insurer.set(report.insurer || '');
          this.policyNumber.set(report.policy_number || '');
          this.incidentType.set(report.incident_type || '');
          this.attendanceDate.set(report.attendance_date || '');
          this.attendanceTime.set(report.attendance_time || '');
          this.technicianName.set(report.technician_name || '');
          this.causeSummary.set(report.cause_summary || '');
          this.workCompleted.set(report.work_completed || '');
          this.damageObserved.set(report.damage_observed || '');
          this.recommendations.set(report.recommendations || '');
          this.limitations.set(report.limitations || '');
          this.reportItems.set((report.items || []).map((item: any) => ({
            id: Number(item.id),
            category: item.category || 'Finding',
            title: item.title || '',
            location: item.location || '',
            description: item.description || '',
            photoUrl: item.photo_data || null,
            photoPreview: item.photo_data ? this.photoPreviewUrl(item.photo_data) : null,
            includeInPdf: item.include_in_pdf !== false
          })));
        },
        error: () => {
          this.isLoadingReport.set(false);
          showToast('error', 'Could not load report');
        }
      });
  }

  openModal(item: ReportItem | null = null, index: number = -1): void {
    this.editingIndex.set(index);
    this.modalCategory.set(item?.category || 'Finding');
    this.modalTitle.set(item?.title || '');
    this.modalLocation.set(item?.location || '');
    this.modalDescription.set(item?.description || '');
    this.modalPhotoBase64.set(item?.photoUrl || null);
    this.modalPhotoPreview.set(item?.photoPreview || (item?.photoUrl ? this.photoPreviewUrl(item.photoUrl) : null));
    this.resetCropState();
    if (this.modalPhotoPreview()) {
      this.cropSourceDataUrl.set(this.modalPhotoPreview());
      this.loadImageInfo(this.modalPhotoPreview()!)
        .then(info => {
          this.cropSourceWidth.set(info.width);
          this.cropSourceHeight.set(info.height);
        })
        .catch(() => undefined);
    }
    this.modalIncludeInPdf.set(item?.includeInPdf ?? true);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.cropEditorOpen.set(false);
    this.cropDragMode = null;
  }

  onFileSelected(event: any): void {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!this.isSupportedImage(file)) {
      showToast('error', 'Please choose a JPG, PNG, or WebP photo.');
      event.target.value = '';
      return;
    }
    if (file.size > this.maxOriginalPhotoBytes) {
      showToast('error', 'This photo is too large. Please choose a photo under 100 MB.');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const sourceDataUrl = String(reader.result);
      this.cropSourceDataUrl.set(sourceDataUrl);
      this.resetCropBox();
      this.loadImageInfo(sourceDataUrl)
        .then(info => {
          this.cropSourceWidth.set(info.width);
          this.cropSourceHeight.set(info.height);
        })
        .catch(() => undefined);
      this.cropEditorOpen.set(true);
    };
    reader.onerror = () => showToast('error', 'Could not read file');
    reader.readAsDataURL(file);
    event.target.value = '';
  }

  openCropEditor(): void {
    const source = this.cropSourceDataUrl() || this.modalPhotoPreview();
    if (!source) return;
    this.cropSourceDataUrl.set(source);
    this.resetCropBox();
    this.cropEditorOpen.set(true);
  }

  closeCropEditor(): void {
    this.cropEditorOpen.set(false);
    this.cropDragMode = null;
  }

  resetCropBox(): void {
    this.cropX.set(10);
    this.cropY.set(10);
    this.cropWidth.set(80);
    this.cropHeight.set(80);
  }

  applyCrop(): void {
    const source = this.cropSourceDataUrl();
    if (!source || this.isPreparingPhoto()) return;
    this.isPreparingPhoto.set(true);
    this.cropImage(source, {
      x: this.cropX(),
      y: this.cropY(),
      width: this.cropWidth(),
      height: this.cropHeight()
    })
      .then(image => {
        if (!this.acceptPreparedPhoto(image.base64)) {
          this.isPreparingPhoto.set(false);
          return;
        }
        this.uploadPreparedPhoto(image.dataUrl).subscribe({
          next: (res) => {
            this.isPreparingPhoto.set(false);
            const path = res?.data?.photo_url;
            if (!res?.success || !path) {
              showToast('error', res?.message || 'Could not prepare photo');
              return;
            }
            this.modalPhotoPreview.set(image.dataUrl);
            this.modalPhotoBase64.set(path);
            this.cropSourceDataUrl.set(image.dataUrl);
            this.loadImageInfo(image.dataUrl)
              .then(info => {
                this.cropSourceWidth.set(info.width);
                this.cropSourceHeight.set(info.height);
              })
              .catch(() => undefined);
            this.cropEditorOpen.set(false);
            showToast('success', 'Image crop applied');
          },
          error: (e) => {
            this.isPreparingPhoto.set(false);
            showToast('error', this.friendlyHttpError(e, 'Could not prepare photo. Please try a smaller image.'));
          }
        });
      })
      .catch(() => {
        this.isPreparingPhoto.set(false);
        showToast('error', 'Could not crop image');
      });
  }

  useFullImage(): void {
    const source = this.cropSourceDataUrl();
    if (!source || this.isPreparingPhoto()) return;
    this.isPreparingPhoto.set(true);
    this.cropImage(source, { x: 0, y: 0, width: 100, height: 100 })
      .then(image => {
        if (!this.acceptPreparedPhoto(image.base64)) {
          this.isPreparingPhoto.set(false);
          return;
        }
        this.uploadPreparedPhoto(image.dataUrl).subscribe({
          next: (res) => {
            this.isPreparingPhoto.set(false);
            const path = res?.data?.photo_url;
            if (!res?.success || !path) {
              showToast('error', res?.message || 'Could not prepare photo');
              return;
            }
            this.modalPhotoPreview.set(image.dataUrl);
            this.modalPhotoBase64.set(path);
            this.cropSourceDataUrl.set(image.dataUrl);
            this.cropEditorOpen.set(false);
            showToast('success', 'Full image added');
          },
          error: (e) => {
            this.isPreparingPhoto.set(false);
            showToast('error', this.friendlyHttpError(e, 'Could not prepare photo. Please try a smaller image.'));
          }
        });
      })
      .catch(() => {
        this.isPreparingPhoto.set(false);
        showToast('error', 'Could not prepare image');
      });
  }

  startCropDrag(event: PointerEvent, mode: CropDragMode): void {
    event.preventDefault();
    event.stopPropagation();
    this.cropDragMode = mode;
    this.cropDragStart = { x: event.clientX, y: event.clientY };
    this.cropDragBox = {
      x: this.cropX(),
      y: this.cropY(),
      width: this.cropWidth(),
      height: this.cropHeight()
    };
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onCropPointerMove(event: PointerEvent): void {
    if (!this.cropDragMode || !this.cropSurface) return;
    event.preventDefault();
    const rect = this.cropSurface.nativeElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const dx = ((event.clientX - this.cropDragStart.x) / rect.width) * 100;
    const dy = ((event.clientY - this.cropDragStart.y) / rect.height) * 100;
    const start = this.cropDragBox;
    const minSize = 8;
    let x = start.x;
    let y = start.y;
    let width = start.width;
    let height = start.height;

    if (this.cropDragMode === 'move') {
      x = this.clampCropValue(start.x + dx, 0, 100 - width);
      y = this.clampCropValue(start.y + dy, 0, 100 - height);
    } else {
      if (this.cropDragMode.includes('w')) {
        x = this.clampCropValue(start.x + dx, 0, start.x + start.width - minSize);
        width = start.x + start.width - x;
      }
      if (this.cropDragMode.includes('e')) {
        width = this.clampCropValue(start.width + dx, minSize, 100 - start.x);
      }
      if (this.cropDragMode.includes('n')) {
        y = this.clampCropValue(start.y + dy, 0, start.y + start.height - minSize);
        height = start.y + start.height - y;
      }
      if (this.cropDragMode.includes('s')) {
        height = this.clampCropValue(start.height + dy, minSize, 100 - start.y);
      }
    }

    this.cropX.set(x);
    this.cropY.set(y);
    this.cropWidth.set(width);
    this.cropHeight.set(height);
  }

  endCropDrag(event?: PointerEvent): void {
    if (event && this.cropDragMode) {
      (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);
    }
    this.cropDragMode = null;
  }

  saveItem(): void {
    if (this.isPreparingPhoto()) {
      showToast('error', 'Please wait for the photo to finish preparing.');
      return;
    }
    const item: ReportItem = {
      id: this.editingIndex() > -1 ? this.reportItems()[this.editingIndex()]?.id : undefined,
      category: this.modalCategory(),
      title: this.modalTitle().trim(),
      location: this.modalLocation().trim(),
      description: this.modalDescription().trim(),
      photoUrl: this.modalPhotoBase64(),
      photoPreview: this.modalPhotoPreview(),
      includeInPdf: this.modalIncludeInPdf()
    };

    if (!item.title && !item.description && !item.photoUrl) {
      showToast('error', 'Add a title, description, or photo before saving the item');
      return;
    }

    const current = [...this.reportItems()];
    if (this.editingIndex() > -1) current[this.editingIndex()] = item;
    else current.push(item);
    this.reportItems.set(current);
    this.closeModal();
    this.saveReport();
  }

  removeItem(index: number): void {
    const current = [...this.reportItems()];
    current.splice(index, 1);
    this.reportItems.set(current);
  }

  moveItem(index: number, direction: -1 | 1): void {
    const next = [...this.reportItems()];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    this.reportItems.set(next);
  }

  refineText(): void {
    if (!this.modalDescription() || this.isRefining()) return;
    this.isRefining.set(true);
    this.http.post<any>(`${this.api.baseUrl}/ai/refine_text.php`, {
      text: this.modalDescription(),
      context: 'Insurance advisory report. Keep wording factual, neutral, and suitable for insurer review.'
    }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        if (res?.success) {
          this.modalDescription.set(res.data.refinedText);
          showToast('success', 'Text refined');
        }
        this.isRefining.set(false);
      },
      error: () => {
        this.isRefining.set(false);
        showToast('error', 'AI refinement failed');
      }
    });
  }

  saveReport(statusOverride?: ReportStatus): void {
    const validation = this.validateReport(false);
    if (validation.length) {
      showToast('error', validation[0]);
      return;
    }

    this.isSaving.set(true);
    this.http.post<any>(`${this.api.baseUrl}/ai/save_insurance_report.php`, this.buildPayload(statusOverride), { headers: (this.api as any).getHeaders() })
      .subscribe({
        next: (res) => {
          this.isSaving.set(false);
          if (res?.success) {
            this.activeReportId.set(Number(res.data.id));
            this.reportNo.set(res.data.report_no);
            if (statusOverride) this.status.set(statusOverride);
            showToast('success', 'Insurance report saved');
          } else {
            showToast('error', res?.message || 'Save failed');
          }
        },
        error: (e) => {
          this.isSaving.set(false);
          showToast('error', this.friendlyHttpError(e, 'Save failed. Please try again.'));
        }
      });
  }

  deleteReport(report: InsuranceReport, event?: Event): void {
    event?.stopPropagation();
    if (!confirm(`Delete ${report.report_no}?`)) return;
    this.http.post<any>(`${this.api.baseUrl}/ai/delete_insurance_report.php`, { id: report.id }, { headers: (this.api as any).getHeaders() })
      .subscribe({
        next: (res) => {
          if (res?.success) {
            if (this.activeReportId() === report.id) this.goBack();
            showToast('success', 'Report deleted');
          } else showToast('error', res?.message || 'Delete failed');
        },
        error: () => showToast('error', 'Delete failed')
      });
  }

  generatePdf(): void {
    const validation = this.validateReport(true);
    if (validation.length) {
      showToast('error', validation[0]);
      return;
    }

    this.isGenerating.set(true);
    this.http.post<any>(`${this.api.baseUrl}/ai/save_insurance_report.php`, this.buildPayload('Finalised'), { headers: (this.api as any).getHeaders() })
      .subscribe({
        next: (res) => {
          if (res?.success) {
            const reportId = Number(res.data.id);
            this.activeReportId.set(reportId);
            this.reportNo.set(res.data.report_no);
            this.status.set('Finalised');
            this.http.get(`${this.api.baseUrl}/ai/insurance_pdf.php?id=${encodeURIComponent(String(reportId))}`, {
              headers: (this.api as any).getHeaders(),
              responseType: 'blob'
            }).subscribe({
              next: (blob) => {
                this.isGenerating.set(false);
                this.downloadPdfBlob(blob, `Insurance_Advisory_${this.reportNo() || reportId}.pdf`);
                showToast('success', 'PDF generated');
              },
              error: (e) => {
                this.isGenerating.set(false);
                this.showPdfError(e);
              }
            });
          } else {
            this.isGenerating.set(false);
            showToast('error', res?.message || 'PDF failed');
          }
        },
        error: (e) => {
          this.isGenerating.set(false);
          showToast('error', this.friendlyHttpError(e, 'PDF failed. Please save again and retry.'));
        }
      });
  }

  emailCustomer(): void {
    const email = this.customerEmail().trim();
    if (!email) {
      showToast('error', 'Add the customer email before sending the report.');
      return;
    }
    if (!this.isValidEmail(email)) {
      showToast('error', 'Please enter a valid customer email address.');
      return;
    }

    const validation = this.validateReport(true);
    if (validation.length) {
      showToast('error', validation[0]);
      return;
    }

    this.isSendingEmail.set(true);
    this.http.post<any>(`${this.api.baseUrl}/ai/save_insurance_report.php`, this.buildPayload('Finalised'), { headers: (this.api as any).getHeaders() })
      .subscribe({
        next: (res) => {
          if (!res?.success || !res.data?.id) {
            this.isSendingEmail.set(false);
            showToast('error', res?.message || 'Could not save report before sending.');
            return;
          }

          const reportId = Number(res.data.id);
          this.activeReportId.set(reportId);
          this.reportNo.set(res.data.report_no);
          this.status.set('Finalised');

          this.api.sendInsuranceReport(reportId).subscribe({
            next: () => {
              this.isSendingEmail.set(false);
              this.status.set('Sent');
              showToast('success', `Report emailed to ${email}`);
            },
            error: (err) => {
              this.isSendingEmail.set(false);
              showToast('error', this.friendlyHttpError(err, 'Could not send insurance report.'));
            }
          });
        },
        error: (e) => {
          this.isSendingEmail.set(false);
          showToast('error', this.friendlyHttpError(e, 'Could not save report before sending.'));
        }
      });
  }

  openAttachModal(): void {
    if (!this.activeReportId()) {
      showToast('error', 'Save the report before attaching it');
      return;
    }
    this.attachModalOpen.set(true);
    this.attachTab.set('Project');
    this.loadAttachList('Project');
  }

  closeAttachModal(): void {
    this.attachModalOpen.set(false);
  }

  setAttachTab(tab: AttachTab): void {
    this.attachTab.set(tab);
    this.loadAttachList(tab);
  }

  attachList(): any[] {
    return this.attachTab() === 'Project' ? this.projects() : this.jobsList();
  }

  referenceTitle(item: any): string {
    return item.title || item.name || item.job_title || item.customer_name || item.address || item.site_address || item.id;
  }

  attachToReference(reference: any): void {
    const id = this.activeReportId();
    if (!id) return;
    this.http.post<any>(`${this.api.baseUrl}/ai/attach_insurance_report.php`, {
      id,
      model_type: this.attachTab(),
      reference_id: reference.id
    }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        if (res?.success) {
          this.referenceId.set(res.data.reference_id);
          this.modelType.set(res.data.model_type);
          this.closeAttachModal();
          showToast('success', 'Report attached');
        } else showToast('error', res?.message || 'Attach failed');
      },
      error: (e) => showToast('error', e.error?.message || 'Attach failed')
    });
  }

  private loadAttachList(tab: AttachTab): void {
    this.isLoadingAttachList.set(true);
    const request = tab === 'Project' ? this.api.getProjects() : this.api.getJobs();
    request.subscribe({
      next: (res: any) => {
        this.isLoadingAttachList.set(false);
        if (tab === 'Project') this.projects.set(res?.data?.projects || []);
        else this.jobsList.set(res?.data?.jobs || []);
      },
      error: () => {
        this.isLoadingAttachList.set(false);
        showToast('error', `Could not load ${tab.toLowerCase()} list`);
      }
    });
  }

  private validateReport(requireFinalFields: boolean): string[] {
    const errors: string[] = [];
    if (requireFinalFields) {
      if (!this.customerName().trim()) errors.push('Customer name is required');
      if (!this.jobAddress().trim()) errors.push('Site address is required');
      if (!this.reportItems().length) errors.push('Add at least one report item');
      if (!this.reportItems().some(item => item.photoUrl && item.description.trim())) errors.push('Add at least one image with a description');
    }
    const oversized = this.reportItems().findIndex(item => this.isInlinePhoto(item.photoUrl) && (item.photoUrl || '').length > this.maxPreparedPhotoBase64Length);
    if (oversized >= 0) errors.push(`Image ${oversized + 1} is too large. Open the image, crop it or choose a smaller photo, then try again.`);
    return errors;
  }

  private buildPayload(statusOverride?: ReportStatus): any {
    return {
      id: this.activeReportId(),
      report_no: this.reportNo(),
      status: statusOverride || this.status(),
      reference_id: this.referenceId(),
      model_type: this.modelType(),
      customer_name: this.customerName(),
      customer_phone: this.customerPhone(),
      customer_email: this.customerEmail(),
      site_address: this.jobAddress(),
      claim_number: this.claimNumber(),
      insurer: this.insurer(),
      policy_number: this.policyNumber(),
      incident_type: this.incidentType(),
      attendance_date: this.attendanceDate(),
      attendance_time: this.attendanceTime(),
      technician_name: this.technicianName(),
      cause_summary: this.causeSummary(),
      work_completed: this.workCompleted(),
      damage_observed: this.damageObserved(),
      recommendations: this.recommendations(),
      limitations: this.limitations(),
      items: this.reportItems().map(item => ({
        category: item.category,
        title: item.title,
        location: item.location,
        description: item.description,
        photoUrl: item.photoUrl,
        includeInPdf: item.includeInPdf
      }))
    };
  }

  private photoPreviewUrl(value: string): string {
    if (value.startsWith('data:image') || /^https?:\/\//i.test(value)) return value;
    if (value.startsWith('uploads/') || value.startsWith('/uploads/')) return this.api.assetUrl(value);
    return `data:image/jpeg;base64,${value}`;
  }

  private resetCropState(): void {
    this.cropSourceDataUrl.set(null);
    this.cropSourceWidth.set(0);
    this.cropSourceHeight.set(0);
    this.cropEditorOpen.set(false);
    this.resetCropBox();
  }

  private clampCropValue(value: string | number, min: number, max: number): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return min;
    return Math.min(Math.max(parsed, min), max);
  }

  private downloadPdfBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.replace(/[^A-Za-z0-9_.-]/g, '_');
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private isSupportedImage(file: File): boolean {
    return ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
  }

  private acceptPreparedPhoto(base64: string): boolean {
    if (base64.length <= this.maxPreparedPhotoBase64Length) return true;
    showToast('error', 'The prepared photo is still too large. Please crop tighter or choose a smaller photo.');
    return false;
  }

  private uploadPreparedPhoto(dataUrl: string) {
    return this.http.post<any>(`${this.api.baseUrl}/ai/upload_insurance_photo.php`, {
      photo_data: dataUrl
    }, { headers: (this.api as any).getHeaders() });
  }

  private isInlinePhoto(value: string | null): boolean {
    if (!value) return false;
    return value.startsWith('data:image') || /^[A-Za-z0-9+\/=\r\n]+$/.test(value);
  }

  private isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private friendlyHttpError(error: any, fallback: string): string {
    const message = error?.error?.message || '';
    if (message) return message;
    if (error?.status === 401) return 'Your login has expired. Please sign in again, then retry the action.';
    if (error?.status === 413) return 'This report is too large to upload. Remove one photo or use smaller/cropped photos, then try again.';
    if (error?.status === 0) return 'The server could not be reached. Please check the connection and try again.';
    if (error?.status >= 500) return 'The server could not complete this action. Please remove any very large photos and try again.';
    return fallback;
  }

  private showPdfError(error: any): void {
    const body = error?.error;
    if (body instanceof Blob) {
      body.text()
        .then(text => {
          try {
            const parsed = JSON.parse(text);
            showToast('error', parsed?.message || this.friendlyHttpError(error, 'PDF failed. Please try again.'));
          } catch {
            showToast('error', this.friendlyHttpError(error, 'PDF failed. Please try again.'));
          }
        })
        .catch(() => showToast('error', this.friendlyHttpError(error, 'PDF failed. Please try again.')));
      return;
    }

    showToast('error', this.friendlyHttpError(error, 'PDF failed. Please try again.'));
  }
}
